import React from 'react';
import { Table } from '../ui/Table';
import styled from '@emotion/styled';
import { Eye } from 'lucide-react';
import AppointmentStatusUpdateContainer from './AppointmentStatusUpdateContainer';

const ImageCell = styled.div`
  width: 64px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 32px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 28px;
  }
  
  img {
    max-width: 100%;
    max-height: 40px;
    border-radius: 8px;
    background: #232325;
    
    @media (max-width: 768px) {
      max-height: 32px;
      border-radius: 6px;
    }
    
    @media (max-width: 480px) {
      max-height: 28px;
      border-radius: 4px;
    }
  }
`;

const ViewsBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #0FC9A7;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  padding: 4px 14px;
  min-width: 56px;
  justify-content: center;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 3px 10px;
    min-width: 48px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 2px 8px;
    min-width: 40px;
    gap: 3px;
  }
`;

const AccentLink = styled.a`
  color: #4BBBE4;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #4BBBE4;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const StatusCell = styled.div`
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
  }
  
  @media (max-width: 480px) {
    min-width: 80px;
  }
`;

const DateCell = styled.div`
  min-width: 100px;
  
  @media (max-width: 768px) {
    min-width: 80px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 70px;
    font-size: 0.85rem;
  }
`;

const TimeSlotCell = styled.div`
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 80px;
    font-size: 0.85rem;
  }
`;

const NameCell = styled.div`
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 80px;
    font-size: 0.85rem;
  }
`;

const EmailCell = styled.div`
  min-width: 150px;
  
  @media (max-width: 768px) {
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
  }
`;

const PhoneCell = styled.div`
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
  }
  
  @media (max-width: 480px) {
    min-width: 80px;
  }
`;

const TitleCell = styled.div`
  min-width: 150px;
  
  @media (max-width: 768px) {
    min-width: 120px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
    font-size: 0.85rem;
  }
`;

const StockCell = styled.div`
  min-width: 80px;
  
  @media (max-width: 768px) {
    min-width: 60px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 50px;
    font-size: 0.85rem;
  }
`;

const PriceCell = styled.div`
  min-width: 100px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    min-width: 80px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 70px;
    font-size: 0.85rem;
  }
`;

const ClickableRow = styled.div`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.05);
  }
`;

const TableContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .table-wrapper {
    flex: 1;
    min-height: 0;
  }
`;

function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const AppointmentsTable = ({ appointments, onAppointmentClick }) => {
    const handleRowClick = (appointment) => {
        if (onAppointmentClick) {
            onAppointmentClick(appointment);
        }
    };

    // Calculate table height to fill available space
    // Subtract header height, padding, and other UI elements
    const tableHeight = 'calc(100vh - 200px)'; // Adjust this value based on your layout

const columns = [
    {
        key: 'number',
        label: 'No.',
            render: (_item, idx) => (
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {String(idx + 1).padStart(2, '0')}
                </span>
            ),
    },
    {
        key: 'image',
        label: 'Image',
        render: (appt) => {
            const vehicle = appt.offer?.vehicle || {};
            return (
                <ImageCell>
                        <img 
                            src={vehicle.images?.[0]?.url || '/car-placeholder.png'} 
                            alt={vehicle.model || 'Car'} 
                            onError={(e) => {
                                e.target.src = '/car-placeholder.png';
                            }}
                        />
                </ImageCell>
            );
        },
    },
    {
        key: 'title',
        label: 'Title',
        render: (appt) => {
            const vehicle = appt.offer?.vehicle || {};
                return (
                    <TitleCell>
                        {vehicle.year && vehicle.make && vehicle.model ? 
                            `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim() : '--'}
                    </TitleCell>
                );
        },
    },    
    {
        key: 'price',
        label: 'Price',
            render: (appt) => (
                <PriceCell>
                    {appt.offer?.amount?.toLocaleString() ? `$${appt.offer?.amount?.toLocaleString()}` : '--'}
                </PriceCell>
            ),
    },  
    {
        key: 'date',
        label: 'Appointment Date',
            render: (appt) => (
                <DateCell>
                    {formatDate(appt.date)}
                </DateCell>
            ),
    },

    {
        key: 'timeSlot',
        label: 'Time Slot',
            render: (appt) => (
                <TimeSlotCell>
                    {appt.timeSlot ? `${appt.timeSlot.start} - ${appt.timeSlot.end}` : '--'}
                </TimeSlotCell>
            ),
    },
    {
        key: 'userName',
        label: 'Name',
            render: (appt) => (
                <NameCell>
                    {appt.offer?.user?.userInfo?.name ? appt.offer?.user?.userInfo?.name : '--'}
                </NameCell>
            ),
    },
    {
        key: 'userEmail',
        label: 'Email',
        render: (appt) => {
            const email = appt.offer?.user?.userInfo?.email;
                return (
                    <EmailCell>
                        {email ? (
                <AccentLink href={`mailto:${email}`}>{email}</AccentLink>
                        ) : '--'}
                    </EmailCell>
                );
        },
    },
    {
        key: 'userPhone',
        label: 'Phone',
        render: (appt) => {
            const phone = appt.offer?.user?.userInfo?.phone;
                return (
                    <PhoneCell>
                        {phone ? (
                <AccentLink href={`tel:${phone}`}>{phone}</AccentLink>
                        ) : '--'}
                    </PhoneCell>
                );
        },
    },
    {
        key: 'stockNo',
        label: 'Stock No.',
            render: (appt) => (
                <StockCell>
                    {appt.offer?.vehicle?.vin ? `#${appt.offer?.vehicle?.vin?.slice(-4)}` : '--'}
                </StockCell>
            ),
    },  
    {
        key: 'status',
        label: 'Status',
            render: (appt) => (
                <StatusCell>
                    <AppointmentStatusUpdateContainer appointment={appt} />
                </StatusCell>
            ),
    },
];

    // Wrap each row with clickable functionality
    const dataWithClickHandlers = appointments.map(appointment => ({
        ...appointment,
        onClick: () => handleRowClick(appointment)
    }));

    return (
        <TableContainer>
        <Table 
            columns={columns} 
            data={dataWithClickHandlers}
                height={tableHeight}
        />
        </TableContainer>
);
};

export default AppointmentsTable; 